import { useEffect, useRef } from 'react';
import { getCookie, setCookie } from '@/modules/common';
import {
  atom,
  bridgeAtom,
  isLiveAgentModeAtom,
  messageTextAtom,
  sessionFinishTimeAtom,
  store,
  useAtom,
  useValue,
} from '@/store';
import type { Getter, Setter } from '@/store';
import { SESSION_FINISH_TIME_STORAGE } from '@/store/constants';
import { SESSION_LIFETIME } from './constants';
import { isDemoUser } from './utils';

const setTimeCookie = (time: number) => {
  const { isSupportSubDomainWithCookie } = store.get(bridgeAtom);

  if (isSupportSubDomainWithCookie) {
    setCookie({
      days: 7,
      value: String(time),
      name: SESSION_FINISH_TIME_STORAGE,
      useRootDomain: true,
    });
  }
};

const setter = (get: Getter, set: Setter, time?: number) => {
  if (!time) {
    const newTime = Date.now() + SESSION_LIFETIME;

    set(sessionFinishTimeAtom, newTime);
    setTimeCookie(newTime);
    return true;
  }

  const timeElapsed = Date.now() - time;

  if (timeElapsed >= SESSION_LIFETIME && !isDemoUser() && !get(isLiveAgentModeAtom)) {
    set(sessionFinishTimeAtom, 0);
    setTimeCookie(0);
    return false;
  }

  // Never reduce the session finish time below what's already stored
  // (prevents history reload from overriding a manually extended session)
  const currentTime = get(sessionFinishTimeAtom);
  const finalTime = Math.max(time, currentTime);
  set(sessionFinishTimeAtom, finalTime);
  setTimeCookie(finalTime);

  return true;
};

const getter = (get: Getter) => {
  let finishTime = get(sessionFinishTimeAtom);

  if (!finishTime) {
    const cookieTime = Number(getCookie(SESSION_FINISH_TIME_STORAGE));

    if (cookieTime) {
      finishTime = cookieTime;
    }
  }

  const timeElapsed = Date.now() - finishTime;

  if (timeElapsed >= SESSION_LIFETIME && !isDemoUser() && !get(isLiveAgentModeAtom)) {
    return 0;
  }

  return finishTime;
};

export const timeAtom = atom(getter, setter);

/**
 * if session is expired, it will return 0.
 *
 * if time is not provided, it will set the finish time to the current time + SESSION_LIFETIME
 *
 * while live agent mode is active the session never expires; ending it grants a fresh
 * SESSION_LIFETIME window from that moment
 * @returns (time?: number) => boolean
 */

export const useSessionFinishTime = (callback?: () => void): ((time?: number) => boolean) => {
  const [time, setTime] = useAtom(timeAtom);

  const messageText = useValue(messageTextAtom);
  const isLiveAgentMode = useValue(isLiveAgentModeAtom);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // give user time to finish message typing
    if (messageText) {
      setTime();
    }
  }, [messageText, setTime]);

  const prevIsLiveAgentModeRef = useRef(isLiveAgentMode);
  useEffect(() => {
    // grant a fresh SESSION_LIFETIME window from the moment live agent mode ends
    if (prevIsLiveAgentModeRef.current && !isLiveAgentMode) {
      setTime();
    }
    prevIsLiveAgentModeRef.current = isLiveAgentMode;
  }, [isLiveAgentMode, setTime]);

  // schedule the callback when a session is finished
  useEffect(() => {
    const timeDifference = time - Date.now();

    if (timeDifference > 0 && !isDemoUser() && !isLiveAgentMode) {
      const timeoutId = setTimeout(() => {
        callbackRef.current?.();
      }, timeDifference);

      // Cleanup the timeout when the component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [time, isLiveAgentMode]);

  return setTime;
};
